/**
 * SESAAT — Troli Marketplace (simpanan sisi pelayar)
 *
 * ⭐ TROLI HANYA MENYIMPAN `productId` + `qty`. TIDAK LEBIH.
 *    Harga, tajuk, gambar dan stok SENTIASA dibaca semula daripada Firestore
 *    semasa halaman troli dibuka, dan harga sebenar dikira SEKALI LAGI di server
 *    semasa bil dicipta.
 *    ⛔ Jangan sekali-kali menyimpan harga di sini. Nilai dalam pelayar boleh
 *       disunting oleh sesiapa; menyimpannya menjemput "beli RM200 dengan RM1",
 *       dan lebih halus lagi: harga yang admin turunkan minggu depan akan kekal
 *       lama dalam troli pelanggan yang tidak pernah dikosongkan.
 *
 * Ia hidup dalam `localStorage`, jadi ia milik SATU pelayar — bukan akaun.
 * Itu memang kelakuan kedai biasa, dan ia percuma (tiada bacaan Firestore).
 * Setiap bacaan/tulisan dibalut try/catch: mod privasi boleh melempar ralat
 * pada localStorage, dan troli yang gagal dibaca tidak boleh merosakkan
 * seluruh halaman marketplace.
 */

const KUNCI = 'sesaat_mkt_troli_v1';
const HAD_JENIS = 20;      // selari dengan pagar server (createMarketplaceBill)
const HAD_KUANTITI = 50;   // selari dengan pagar server

const pendengar = new Set();

function baca() {
    try {
        const mentah = localStorage.getItem(KUNCI);
        if (!mentah) return [];
        const data = JSON.parse(mentah);
        if (!Array.isArray(data)) return [];
        // Tapis nilai rosak di SINI, bukan di tempat guna. Troli yang disunting
        // tangan (atau tinggalan versi lama) tidak boleh menghantar `qty: "abc"`
        // ke server.
        return data
            .map((b) => ({
                productId: String(b?.productId || '').trim(),
                qty: Math.min(HAD_KUANTITI, Math.max(1, Math.floor(Number(b?.qty) || 1))),
            }))
            .filter((b) => b.productId)
            .slice(0, HAD_JENIS);
    } catch {
        return [];
    }
}

function tulis(senarai) {
    try {
        localStorage.setItem(KUNCI, JSON.stringify(senarai));
    } catch {
        /* mod privasi / simpanan penuh — troli hilang, tetapi halaman kekal hidup */
    }
    pendengar.forEach((fn) => { try { fn(senarai); } catch { /* satu pendengar rosak tidak boleh menghentikan yang lain */ } });
}

/** Isi troli semasa: `[{ productId, qty }]`. */
export function bacaTroli() {
    return baca();
}

/** Jumlah unit dalam troli (untuk lencana pada ikon). */
export function jumlahUnit() {
    return baca().reduce((n, b) => n + b.qty, 0);
}

/** Bilangan JENIS barang (bukan unit). */
export function jumlahJenis() {
    return baca().length;
}

/**
 * Tambah barang. Kalau ia sudah ada, kuantiti DIGABUNGKAN — bukan diganti.
 * Pulangkan kuantiti akhir barang itu, supaya pemanggil boleh memberitahu
 * pengguna apa yang sebenarnya berlaku.
 */
export function tambahKeTroli(productId, qty = 1) {
    const id = String(productId || '').trim();
    if (!id) return 0;
    const senarai = baca();
    const tambah = Math.max(1, Math.floor(Number(qty) || 1));
    const ada = senarai.find((b) => b.productId === id);
    if (ada) {
        ada.qty = Math.min(HAD_KUANTITI, ada.qty + tambah);
    } else {
        if (senarai.length >= HAD_JENIS) return -1;   // -1 = troli penuh
        senarai.push({ productId: id, qty: Math.min(HAD_KUANTITI, tambah) });
    }
    tulis(senarai);
    return senarai.find((b) => b.productId === id).qty;
}

/** Tetapkan kuantiti tepat. Kuantiti 0 atau kurang membuang barang itu. */
export function tetapKuantiti(productId, qty) {
    const id = String(productId || '').trim();
    const n = Math.floor(Number(qty) || 0);
    if (n < 1) return buangDariTroli(id);
    const senarai = baca();
    const ada = senarai.find((b) => b.productId === id);
    if (!ada) return;
    ada.qty = Math.min(HAD_KUANTITI, n);
    tulis(senarai);
}

export function buangDariTroli(productId) {
    const id = String(productId || '').trim();
    tulis(baca().filter((b) => b.productId !== id));
}

export function kosongkanTroli() {
    tulis([]);
}

/**
 * Langgan perubahan troli. Pulangkan fungsi untuk berhenti melanggan.
 *
 * ⚠️ Acara `storage` pelayar hanya menyala pada tab LAIN, bukan tab yang
 *    menulis. Sebab itu `tulis()` di atas memanggil pendengar sendiri — tanpa
 *    kedua-duanya, lencana troli sama ada tidak bergerak dalam tab semasa,
 *    atau tidak segerak antara dua tab yang terbuka.
 */
export function langganTroli(fn) {
    pendengar.add(fn);
    const dariTabLain = (e) => { if (e.key === KUNCI) fn(baca()); };
    try { window.addEventListener('storage', dariTabLain); } catch { /* SSR */ }
    return () => {
        pendengar.delete(fn);
        try { window.removeEventListener('storage', dariTabLain); } catch { /* SSR */ }
    };
}

export const HAD_TROLI = { jenis: HAD_JENIS, kuantiti: HAD_KUANTITI };
